/**
 * The MEMBER-facing INVOCATION RPC definitions (OL-114).
 *
 * An **invocation** is one execution of a loop (`orcaloops.outcomes`), so the
 * rpc names in this group keep the engine's own spelling: `outcomes.*`. The
 * living definition it was rendered from lives in the sibling
 * `loop-definitions.rpc.ts` group.
 *
 * This is the actor-scoped counterpart to `admin/orcaloops-outcomes.rpc.ts` —
 * "the member-facing, actor-scoped `outcomes.*` group that arrives later" its
 * header names. It sits behind {@link LoopActorMiddleware}, which requires no
 * role, and:
 *
 * **`customerId` on a payload here is a SELECTOR among the caller's own
 * memberships, never an authority.** It is `Schema.optional` because the common
 * case — one membership, or a pinned `ocl_` token — needs no selector at all,
 * and `resolveActorCustomer` (`loop-scope.ts`) is the only thing that turns a
 * request into a customer scope.
 *
 * This group is pre-created by the seam issue with a single `outcomes.cancel`
 * rpc so the wiring (registration in `orcaloops-routes.ts`, the handlers layer,
 * the re-exports) is already in place. The invocation issues ADD rpcs to this
 * same group; they do not create a new one.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Outcome, OutcomeStatus } from "@fm/db/models/Outcome";
import { Report } from "@fm/db/models/Report";
import { Run, RunDecision } from "@fm/db/models/Run";
import { Trigger } from "@fm/db/models/Trigger";
import { Schema } from "effect";

import { HumanWaitNotResolvableError, ResolveHumanWaitResult } from "../admin/orcaloops-human-waits.rpc.ts";
import { OutcomeFireResponse, TriggerSpecInput } from "../admin/orcaloops-outcomes.rpc.ts";
import {
  DatabaseError,
  Forbidden,
  InvalidStateTransition,
  RecordNotFoundError,
  Unauthorized,
  ValidationError,
} from "../errors.ts";
import { LoopActorMiddleware } from "../loop-actor.tags.ts";
import { LoopInvocationRow } from "./loop-definitions.rpc.ts";

/**
 * One invocation addressed by id.
 *
 * The member-facing twin of `OutcomeRefInput` (`admin/orcaloops-outcomes.rpc.ts`),
 * whose `customerId` is REQUIRED because an operator acts across customers. Here
 * it is optional and merely selects among the caller's own memberships.
 *
 * @since 1.0.0
 */
export const LoopOutcomeRefInput = Schema.Struct({
  outcomeId: Schema.String,
  customerId: Schema.optional(Schema.String),
});
export type LoopOutcomeRefInput = Schema.Schema.Type<typeof LoopOutcomeRefInput>;

/**
 * One keyset page of the caller's executions.
 *
 * Paginated BY CONTRACT, not as an MCP afterthought: the outcome-surface
 * screens page exactly the same way, so there is never a second shape to
 * maintain. `loopId` narrows to one definition's executions.
 *
 * @since 1.0.0
 */
export const LoopInvocationListInput = Schema.Struct({
  /** Default 20, clamped to 100. */
  limit: Schema.optional(Schema.Int),
  cursor: Schema.optional(Schema.String),
  status: Schema.optional(OutcomeStatus),
  loopId: Schema.optional(Schema.String),
  customerId: Schema.optional(Schema.String),
});
export type LoopInvocationListInput = Schema.Schema.Type<typeof LoopInvocationListInput>;

/**
 * A keyset page: the rows, the cursor that resumes after them, and whether
 * resuming would find anything.
 *
 * The row is {@link LoopInvocationRow}, IMPORTED from the definition group
 * rather than redeclared, so there is one compact-invocation row in the
 * codebase rather than two that drift.
 *
 * @since 1.0.0
 */
export const LoopInvocationListResponse = Schema.Struct({
  invocations: Schema.Array(LoopInvocationRow),
  cursor: Schema.NullOr(Schema.String),
  hasMore: Schema.Boolean,
});
export type LoopInvocationListResponse = Schema.Schema.Type<typeof LoopInvocationListResponse>;

/**
 * Which optional panels a detail read should carry.
 *
 * `outcomes` is the REPORT VERDICTS panel — a uri and a pass/fail per report,
 * never a body. The body is `outcomes.report`, fetched by a caller that decided
 * it wanted it.
 *
 * @since 1.0.0
 */
export const LoopInvocationInclude = Schema.Literal("steps", "outcomes", "triggers", "decisions");
export type LoopInvocationInclude = Schema.Schema.Type<typeof LoopInvocationInclude>;

/**
 * One execution addressed by id, with the panels and the turn window the caller
 * wants.
 *
 * `include` defaults to `["triggers"]` — the cheap answer. `fromTurn` / `toTurn`
 * window the step list, which is then hard-capped, so a long-running loop can
 * never return an unbounded tree.
 *
 * @since 1.0.0
 */
export const LoopInvocationGetInput = Schema.Struct({
  outcomeId: Schema.String,
  include: Schema.optional(Schema.Array(LoopInvocationInclude)),
  fromTurn: Schema.optional(Schema.Int),
  toTurn: Schema.optional(Schema.Int),
  customerId: Schema.optional(Schema.String),
});
export type LoopInvocationGetInput = Schema.Schema.Type<typeof LoopInvocationGetInput>;

/**
 * A step row.
 *
 * `Run.json` ALREADY carries `prRepo` / `prNumber` (`tables/runs.ts`), so no
 * schema extension is needed — only the VALUES are overridden from the
 * `orca_tasks` join. The omit is the non-negotiable part: `Run.json` carries
 * `deferredToken` (the plaintext `@effect/workflow` DurableDeferred token, which
 * `packages/db/src/tables/runs.ts` declares "never leaves the api" — holding it
 * lets you resolve the workflow directly) and `callbackTokenHash` (the run's
 * capability digest). Neither is needed to render a step tree, and this is a
 * TOKEN-AUTHED surface. Struct-omit rather than a hand-listed field set, so a
 * new column cannot leak one later.
 *
 * @since 1.0.0
 */
export const LoopStepRow = Run.json.pipe(Schema.omit("deferredToken", "callbackTokenHash"));
export type LoopStepRow = Schema.Schema.Type<typeof LoopStepRow>;

/**
 * One report verdict as `include: ["outcomes"]` renders it — never a body.
 *
 * @since 1.0.0
 */
export const LoopOutcomeRow = Schema.Struct({
  reportId: Schema.String,
  stepId: Schema.String,
  /** `null` when the report carries no verdict — `Report.verdict` is nullable. */
  passed: Schema.NullOr(Schema.Boolean),
  createdAt: Schema.DateTimeUtc,
  /** Where the BODY lives, for a caller that decides it wants it. */
  uri: Schema.String,
});
export type LoopOutcomeRow = Schema.Schema.Type<typeof LoopOutcomeRow>;

/**
 * One orchestrator turn's decision, as `include: ["decisions"]` renders it.
 * Oldest first, so the last entry is the terminal decision.
 *
 * @since 1.0.0
 */
export const LoopDecisionRow = Schema.Struct({
  stepId: Schema.String,
  turn: Schema.Number,
  decision: RunDecision,
  createdAt: Schema.DateTimeUtc,
});
export type LoopDecisionRow = Schema.Schema.Type<typeof LoopDecisionRow>;

/**
 * How many of an execution's steps are live, parked or broken.
 *
 * @since 1.0.0
 */
export const LoopRunCounts = Schema.Struct({
  running: Schema.Number,
  waiting: Schema.Number,
  failed: Schema.Number,
});
export type LoopRunCounts = Schema.Schema.Type<typeof LoopRunCounts>;

/**
 * One execution in full: always the row, its run tallies and the LATEST
 * orchestrator decision; then whatever `include` asked for.
 *
 * `triggers` is `[]` rather than absent when it was not included — a caller
 * reading the field never has to distinguish "none armed" from "not asked for",
 * because the default `include` always asks for it.
 *
 * @since 1.0.0
 */
export const LoopInvocationDetailResponse = Schema.Struct({
  invocation: Outcome.json,
  triggers: Schema.Array(Trigger.json),
  runCounts: LoopRunCounts,
  latestDecision: Schema.NullOr(RunDecision),
  steps: Schema.optional(Schema.Array(LoopStepRow)),
  outcomes: Schema.optional(Schema.Array(LoopOutcomeRow)),
  decisions: Schema.optional(Schema.Array(LoopDecisionRow)),
});
export type LoopInvocationDetailResponse = Schema.Schema.Type<typeof LoopInvocationDetailResponse>;

/**
 * One report addressed by id — the ONE rpc that serves a report BODY.
 *
 * @since 1.0.0
 */
export const LoopReportRefInput = Schema.Struct({
  reportId: Schema.String,
  customerId: Schema.optional(Schema.String),
});
export type LoopReportRefInput = Schema.Schema.Type<typeof LoopReportRefInput>;

/**
 * The report, body and all. `Report.json` is the model — never redeclared here.
 *
 * @since 1.0.0
 */
export const LoopReportResponse = Schema.Struct({ report: Report.json });
export type LoopReportResponse = Schema.Schema.Type<typeof LoopReportResponse>;

/**
 * The caller's own inbox read: every run parked on a person.
 *
 * @since 1.0.0
 */
export const LoopInboxInput = Schema.Struct({
  customerId: Schema.optional(Schema.String),
});
export type LoopInboxInput = Schema.Schema.Type<typeof LoopInboxInput>;

/**
 * One parked question, as the member inbox renders it.
 *
 * The `OutcomeInboxRow` shape (`admin/orcaloops-human-waits.rpc.ts`) — `Run.json`
 * minus the same two secrets, for the same reasons — extended with what a person
 * needs to answer WITHOUT a second read: which invocation is asking, what the
 * question is, which answers are offered, and when the answer stops mattering.
 *
 * There is no `deadline_at` column anywhere in the schema. `deadlineAt` is
 * DERIVED from the PARENT orchestrator run's `decision.question.timeout_hours`,
 * and is `null` whenever the parent, its decision, its question or that field is
 * absent.
 *
 * @since 1.0.0
 */
export const LoopInboxRow = Schema.extend(
  LoopStepRow,
  Schema.Struct({
    outcomeTitle: Schema.String,
    question: Schema.NullOr(Schema.String),
    options: Schema.Array(Schema.String),
    deadlineAt: Schema.NullOr(Schema.DateTimeUtc),
  }),
);
export type LoopInboxRow = Schema.Schema.Type<typeof LoopInboxRow>;

/**
 * One full-text query across definitions, executions and reports.
 *
 * `kind` narrows WHICH indexes are read; `status` narrows the hits that carry
 * one, which today is only an execution.
 *
 * @since 1.0.0
 */
export const LoopSearchInput = Schema.Struct({
  query: Schema.String,
  kind: Schema.optional(Schema.Literal("definition", "invocation", "report")),
  status: Schema.optional(Schema.String),
  /** Default 10, clamped to 50. */
  limit: Schema.optional(Schema.Int),
  customerId: Schema.optional(Schema.String),
});
export type LoopSearchInput = Schema.Schema.Type<typeof LoopSearchInput>;

/**
 * One hit, whichever of the three corpora it came from.
 *
 * @since 1.0.0
 */
export const LoopSearchHit = Schema.Struct({
  kind: Schema.Literal("definition", "invocation", "report"),
  id: Schema.String,
  loopId: Schema.NullOr(Schema.String),
  invocationId: Schema.NullOr(Schema.String),
  title: Schema.String,
  /** `ts_headline` output, capped at 200 characters. */
  snippet: Schema.String,
  status: Schema.NullOr(Schema.String),
  at: Schema.DateTimeUtc,
  uri: Schema.NullOr(Schema.String),
});
export type LoopSearchHit = Schema.Schema.Type<typeof LoopSearchHit>;

/**
 * One page of the change feed. An ABSENT cursor means "start from now" — never
 * a backlog dump.
 *
 * @since 1.0.0
 */
export const LoopSinceInput = Schema.Struct({
  cursor: Schema.optional(Schema.String),
  limit: Schema.optional(Schema.Int),
  customerId: Schema.optional(Schema.String),
});
export type LoopSinceInput = Schema.Schema.Type<typeof LoopSinceInput>;

/**
 * One thing that moved.
 *
 * This is a CHANGE FEED, not an event log: a row updated twice inside one
 * polling window appears once, at its latest instant.
 *
 * @since 1.0.0
 */
export const LoopSinceEvent = Schema.Struct({
  at: Schema.DateTimeUtc,
  invocationId: Schema.String,
  loopId: Schema.NullOr(Schema.String),
  kind: Schema.Literal("invocation_status", "step_status", "report", "human_wait"),
  id: Schema.String,
  status: Schema.NullOr(Schema.String),
  title: Schema.NullOr(Schema.String),
  uri: Schema.NullOr(Schema.String),
});
export type LoopSinceEvent = Schema.Schema.Type<typeof LoopSinceEvent>;

/**
 * A page of the feed plus the cursor that resumes strictly after it.
 *
 * @since 1.0.0
 */
export const LoopSinceResponse = Schema.Struct({
  events: Schema.Array(LoopSinceEvent),
  cursor: Schema.String,
  hasMore: Schema.Boolean,
});
export type LoopSinceResponse = Schema.Schema.Type<typeof LoopSinceResponse>;

/**
 * The fields BOTH arms of {@link LoopInvocationCreateInput} carry.
 *
 * `draftId` rides here rather than on the structured arm alone, so a RENDERED
 * invoke is idempotent exactly the way a structured one is: the MCP write-tools
 * surface passes back the `draftId` a `loops_define` phase-2 result echoed, and
 * a timed-out retry of that same rendered invoke must return the original
 * execution rather than buy a second budget.
 *
 * @since 1.0.0
 */
const CreateCommon = {
  customerId: Schema.optional(Schema.String),
  /** The extractor draft this confirm came from. Makes the create idempotent. */
  draftId: Schema.optional(Schema.String),
  /** Create the row and arm its trigger WITHOUT dispatching attempt 1. */
  arm: Schema.optional(Schema.Boolean),
};

/**
 * An AD-HOC invocation: the whole statement stated inline, rendered from no
 * definition at all. `loop_id`, `loop_version` and `inputs` all land null.
 *
 * @since 1.0.0
 */
export const StructuredInvocationInput = Schema.Struct({
  title: Schema.String,
  statement: Schema.String,
  acceptance: Schema.NullOr(Schema.String),
  repo: Schema.String,
  ref: Schema.String,
  budgetUsd: Schema.Number,
  maxParallel: Schema.optional(Schema.Int),
  maxTurns: Schema.optional(Schema.Int),
  trigger: Schema.optional(TriggerSpecInput),
  ...CreateCommon,
});
export type StructuredInvocationInput = Schema.Schema.Type<typeof StructuredInvocationInput>;

/**
 * An invocation RENDERED from a living definition.
 *
 * `{ loopId, inputs, version? }` is the WHOLE payload of this arm — there are
 * deliberately no per-invocation overrides of a definition's defaults. Changing
 * a default is `loops.update`, which costs nothing; an execution that needs a
 * one-off budget is an ad-hoc invocation.
 *
 * @since 1.0.0
 */
export const LoopRenderedInvocationInput = Schema.Struct({
  loopId: Schema.String,
  inputs: Schema.Record({ key: Schema.String, value: Schema.String }),
  /**
   * Pin an older, frozen version of this LIVING definition. Absent = render from
   * the head as it stands right now, which is the ordinary path — and the reason
   * refining a loop is useful at all, since the next invocation picks the
   * refinement up.
   */
  version: Schema.optional(Schema.Int),
  trigger: Schema.optional(TriggerSpecInput),
  ...CreateCommon,
});
export type LoopRenderedInvocationInput = Schema.Schema.Type<typeof LoopRenderedInvocationInput>;

/**
 * One create, either way it was stated.
 *
 * An UNTAGGED union of two structs with DISJOINT required fields, so decoding
 * is unambiguous without a discriminator the caller would have to remember: a
 * `{ loopId, inputs }` body cannot satisfy the structured member (it carries no
 * `title`), and a structured body cannot satisfy the rendered member (it carries
 * no `loopId`).
 *
 * @since 1.0.0
 */
export const LoopInvocationCreateInput = Schema.Union(StructuredInvocationInput, LoopRenderedInvocationInput);
export type LoopInvocationCreateInput = Schema.Schema.Type<typeof LoopInvocationCreateInput>;

/**
 * The created invocation, whether its first attempt was dispatched, the trigger
 * that was armed, and where the caller's change feed should start.
 *
 * @since 1.0.0
 */
export const LoopInvocationCreateResponse = Schema.Struct({
  outcome: Outcome.json,
  /** `null` when `arm: true` — nothing was dispatched. */
  attempt: Schema.NullOr(Schema.String),
  trigger: Schema.optional(Trigger.json),
  /** Taken BEFORE the create, so the first `outcomes.since` starts at this invocation. */
  cursor: Schema.String,
});
export type LoopInvocationCreateResponse = Schema.Schema.Type<typeof LoopInvocationCreateResponse>;

/**
 * Answer one parked question of the caller's own.
 *
 * `ResolveHumanWaitInput` (`admin/orcaloops-human-waits.rpc.ts`) with
 * `customerId` made OPTIONAL — on this surface it is a selector among the
 * caller's own memberships, never an authority.
 *
 * @since 1.0.0
 */
export const LoopResolveHumanWaitInput = Schema.Struct({
  runId: Schema.String,
  reply: Schema.String,
  option: Schema.optional(Schema.String),
  customerId: Schema.optional(Schema.String),
});
export type LoopResolveHumanWaitInput = Schema.Schema.Type<typeof LoopResolveHumanWaitInput>;

/**
 * The error union every READ in this group declares.
 *
 * `ValidationError` is not optional: `resolveActorCustomer` fails it on an
 * ambiguous membership set, and `decodeCursor` fails it on a malformed cursor.
 */
const ReadErrors = Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError);

/**
 * The error union the writes that never transition an execution declare. The
 * same five as {@link ReadErrors} and named apart because these WRITE — a
 * reader changing one union must not silently change the other.
 */
const WriteErrors = Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError);

/**
 * {@link WriteErrors} plus `InvalidStateTransition`, for the two writes that
 * wrap an admin function which raises it.
 */
const TransitionErrors = Schema.Union(
  Unauthorized,
  Forbidden,
  RecordNotFoundError,
  ValidationError,
  DatabaseError,
  InvalidStateTransition,
);

/**
 * The member-facing invocation RPC group. Every member carries
 * {@link LoopActorMiddleware}.
 *
 * The wire row is `Outcome.json` from `@fm/db/models/Outcome` — the model IS the
 * wire type and is never redeclared here.
 *
 * @since 1.0.0
 */
export class LoopsRpcs extends RpcGroup.make(
  Rpc.make("outcomes.cancel", {
    payload: LoopOutcomeRefInput,
    success: Outcome.json,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      ValidationError,
      DatabaseError,
      InvalidStateTransition,
    ),
  }).middleware(LoopActorMiddleware),
  Rpc.make("outcomes.list", {
    payload: LoopInvocationListInput,
    success: LoopInvocationListResponse,
    error: ReadErrors,
  }).middleware(LoopActorMiddleware),
  Rpc.make("outcomes.get", {
    payload: LoopInvocationGetInput,
    success: LoopInvocationDetailResponse,
    error: ReadErrors,
  }).middleware(LoopActorMiddleware),
  Rpc.make("outcomes.report", {
    payload: LoopReportRefInput,
    success: LoopReportResponse,
    error: ReadErrors,
  }).middleware(LoopActorMiddleware),
  Rpc.make("outcomes.inbox", {
    payload: LoopInboxInput,
    success: Schema.Array(LoopInboxRow),
    error: ReadErrors,
  }).middleware(LoopActorMiddleware),
  Rpc.make("outcomes.search", {
    payload: LoopSearchInput,
    success: Schema.Array(LoopSearchHit),
    error: ReadErrors,
  }).middleware(LoopActorMiddleware),
  Rpc.make("outcomes.since", {
    payload: LoopSinceInput,
    success: LoopSinceResponse,
    error: ReadErrors,
  }).middleware(LoopActorMiddleware),
  Rpc.make("outcomes.create", {
    payload: LoopInvocationCreateInput,
    success: LoopInvocationCreateResponse,
    error: WriteErrors,
  }).middleware(LoopActorMiddleware),
  Rpc.make("outcomes.resume", {
    payload: LoopOutcomeRefInput,
    success: Outcome.json,
    error: TransitionErrors,
  }).middleware(LoopActorMiddleware),
  // Deliberately NOT named `pause` / `resume`: `outcomes.resume` above resumes
  // the WORKFLOW EXECUTION, and these two only flip `triggers.enabled` — the
  // same reasoning `admin/orcaloops-outcomes.rpc.ts` records for the admin group.
  Rpc.make("outcomes.pauseTriggers", {
    payload: LoopOutcomeRefInput,
    success: Schema.Array(Trigger.json),
    error: WriteErrors,
  }).middleware(LoopActorMiddleware),
  Rpc.make("outcomes.resumeTriggers", {
    payload: LoopOutcomeRefInput,
    success: Schema.Array(Trigger.json),
    error: WriteErrors,
  }).middleware(LoopActorMiddleware),
  Rpc.make("outcomes.fire", {
    payload: LoopOutcomeRefInput,
    success: OutcomeFireResponse,
    error: TransitionErrors,
  }).middleware(LoopActorMiddleware),
  Rpc.make("outcomes.resolveHumanWait", {
    payload: LoopResolveHumanWaitInput,
    success: ResolveHumanWaitResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      ValidationError,
      HumanWaitNotResolvableError,
      DatabaseError,
    ),
  }).middleware(LoopActorMiddleware),
) {}
